import type * as React from 'react';
import { useState } from 'react';
import { useBelowMobile } from '../../lib/useBelowMobile.ts';
import { useVisibleArea } from '../../lib/useVisibleArea.ts';
import { CHAT_STRIP_WIDTH, chatDrawerDefaultWidth } from './chatDrawerWidth.ts';

interface ChatDrawerLayoutOptions {
  open: boolean;
  expanded: boolean;
  /** How wide the caller wants it, or undefined for the width the design picks. */
  width: number | undefined;
}

/** Where the chat drawer stands and how big it is drawn, on this screen. */
export function useChatDrawerLayout({ open, expanded, width }: ChatDrawerLayoutOptions) {
  const belowMobile = useBelowMobile();
  /*
   * The width for a drawer nobody has sized, worked out once at mount: it is
   * the width the drawer opens at, and a window being dragged wider
   * mid-conversation must not make the panel creep along with it.
   */
  const [openingWidth] = useState(chatDrawerDefaultWidth);
  const drawnWidth = width ?? openingWidth;
  /*
   * Covering a phone, it is sized to what is visible rather than to the window:
   * the iOS keyboard pans a full-height fixed panel up, header and all, and the
   * composer jumps with every line typed.
   */
  const area = useVisibleArea(belowMobile && open);
  /* A phone has no room for a strip, so there it goes all the way out. */
  const parked = belowMobile
    ? 'translate-x-full'
    : 'translate-x-[calc(100%-var(--chat-strip))]';
  /*
   * Both widths are lengths from the right edge, so the width transition can
   * interpolate between them; a `left` inset or a `max-width` cap would snap.
   * Full screen stops at the rail's `--rail-w`; `100%` skips the scrollbar, `100vw` would not.
   */
  const fullScreenWidth = belowMobile ? '100%' : 'calc(100% - var(--rail-w))';
  const columnWidth = belowMobile ? undefined : `min(${drawnWidth}px, 92vw)`;

  const style = {
    width: expanded ? fullScreenWidth : columnWidth,
    '--chat-strip': `${CHAT_STRIP_WIDTH}px`,
    ...(area ? { top: area.top, height: area.height, bottom: 'auto' } : {}),
  } as React.CSSProperties;

  return {
    belowMobile,
    openingWidth,
    drawnWidth,
    /* Expanded, or on a phone: either way it is no longer a column of its own width. */
    filling: expanded || belowMobile,
    keyboard: area?.keyboard ?? false,
    parked,
    style,
  };
}
